import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { dashApi } from "../api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await dashApi.post("/auth/forgot-password", { email: email.trim() });
    } catch {
      // Backend deliberately always returns OK to avoid leaking which emails exist.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-900 flex items-center justify-center p-4"
      data-testid="dashboard-forgot-password-page"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-blue-600 mb-3">
            <Mail className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-white">Reset your password</h1>
          <p className="text-sm text-slate-400 mt-1">
            We'll email you a secure link to choose a new one.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="text-sm text-slate-700">
                If <strong>{email}</strong> matches an account, a reset link has been sent.
                Check your inbox (and spam folder) — the link is valid for 1 hour.
              </div>
              <Link
                to="/dashboard/login"
                className="inline-flex items-center text-sm text-blue-700 hover:underline"
                data-testid="forgot-password-back-to-login"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email-input"
                  placeholder="you@vangelder-inc.com"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="forgot-password-submit-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send reset link
              </Button>
              <div className="text-center">
                <Link
                  to="/dashboard/login"
                  className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center"
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
